import {
  ENVIRONMENTAL_HAZARDS,
  EnvironmentalHazardOption,
  HAZMAT_CLASSIFICATION,
  HazmatOption,
  INCIDENT_NEEDS,
  IncidentNeedOption,
  OTHER_HAZARDS,
  OtherHazardOption,
  PHYSICAL_HAZARDS,
  PhysicalHazardOption,
  POTENTIAL_HAZARDS,
  PotentialHazardOption,
  REQUIRED_PPE,
  RequiredPPEOption,
} from './initialResponsePage/SafetyAnalysis/types';
import {
  PLANNING_PHASE,
  PlanningPhaseType,
  RESPONSIBLE_ROLE,
  ResponsibleRoleType,
} from './planningView/USCGPage';

export const PHYSICAL_HAZARD_OPTIONS: PhysicalHazardOption[] =
  PHYSICAL_HAZARDS.map((hazard) => ({
    label: hazard,
    value: hazard,
  }));

export const ENVIRONMENTAL_HAZARD_OPTIONS: EnvironmentalHazardOption[] =
  ENVIRONMENTAL_HAZARDS.map((hazard) => ({
    label: hazard,
    value: hazard,
  }));

export const OTHER_HAZARD_OPTIONS: OtherHazardOption[] = OTHER_HAZARDS.map(
  (hazard) => ({
    label: hazard,
    value: hazard,
  })
);

export const REQUIRED_PPE_OPTIONS: RequiredPPEOption[] = REQUIRED_PPE.map(
  (PPE) => ({
    label: PPE,
    value: PPE,
  })
);

export const HAZMAT_CLASSIFICATION_OPTIONS: HazmatOption[] =
  HAZMAT_CLASSIFICATION.map((HAZMAT) => ({ label: HAZMAT, value: HAZMAT }));

export const POTENTIAL_HAZARD_OPTIONS: PotentialHazardOption[] =
  POTENTIAL_HAZARDS.map((hazard) => ({
    label: hazard,
    value: hazard,
  }));

export const INCIDENT_NEED_OPTIONS: IncidentNeedOption[] = INCIDENT_NEEDS.map(
  (requirement) => ({
    label: requirement,
    value: requirement,
  })
);

/**
 * Canonical options plus a custom option for each stored value the canonical
 * list doesn't contain. The Safety Analysis DB columns are unconstrained
 * text[], and the DGPT agent may persist values outside the controlled
 * vocabulary — rendering them as chips (instead of silently hiding them) both
 * displays approved AI suggestions and prevents the next manual edit from
 * erasing them.
 */
export const withStoredValues = (
  options: ReadonlyArray<{ label: string; value: string }>,
  stored: readonly string[] | null | undefined
): { label: string; value: string }[] => {
  const known = new Set<string>(options.map((option) => option.value));
  const custom = [...new Set(stored ?? [])]
    .filter((value) => !known.has(value))
    .map((value) => ({ label: value, value }));
  return [...options, ...custom];
};

export const ACTION_STATUS_OPTIONS = [
  { label: 'Proposed', value: 'Proposed' },
  { label: 'Planned', value: 'Planned' },
  { label: 'Current/Ongoing', value: 'Current/Ongoing' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Closed', value: 'Closed' },
];

export const RR_STATUS_OPTIONS = [
  {
    label: 'Awaiting Approval',
    value: 'Awaiting Approval',
    hint: 'All necessary personnel must mark their approval for the Resource Request to be Approved and allow the purchase to be conducted.',
  },
  { label: 'Approved', value: 'Approved', hint: '' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Complete', value: 'Complete' },
  { label: 'Incomplete', value: 'Incomplete' },
];

export const RRI_STATUS_OPTIONS = [
  { label: 'Not Started', value: 'Not Started' },
  { label: 'Ordered', value: 'Ordered' },
  { label: 'Arrived', value: 'Arrived' },
  { label: 'Cancelled', value: 'Cancelled' },
  { label: 'Incomplete', value: 'Incomplete' },
];

export const RESOURCE_SITE_TYPES = [
  { label: 'Staging Area', value: 'Staging Area' },
  { label: 'Incident Command Post', value: 'Incident Command Post' },
  { label: 'Warehouse', value: 'Warehouse' },
  { label: 'Hotel/Lodging', value: 'Hotel/Lodging' },
  { label: 'AP (Assembly Point)', value: 'AP (Assembly Point)' },
  { label: 'RC (Relocation Center)', value: 'RC (Relocation Center)' },
  {
    label: 'APOE (Air Point of Embarkation)',
    value: 'APOE (Air Point of Embarkation)',
  },
  {
    label: 'SPOE (Sea Point of Embarkation)',
    value: 'SPOE (Sea Point of Embarkation)',
  },
];

export const RESOURCE_SET_STATUSES = [
  { label: 'Onsite', value: 'Onsite' },
  { label: 'Deployed', value: 'Deployed' },
  { label: 'In Transit', value: 'In Transit' },
];

export const STRIKE_TEAM_STATUSES = ['Onsite', 'Deployed', 'In Transit'];

export const STRIKE_TEAM_STATUSES_OBJECT = [
  { label: 'Onsite', value: 'Onsite' },
  { label: 'Deployed', value: 'Deployed' },
  { label: 'In Transit', value: 'In Transit' },
];

export const COMS_TYPES = [
  { label: 'Radio', value: 'Radio' },
  { label: 'Nonradio', value: 'Nonradio' },
];

export const COMS_FUNCTIONS = [
  { label: 'Command', value: 'Command' },
  { label: 'Tactical', value: 'Tactical' },
  { label: 'Ground-to-Air', value: 'Ground-to-Air' },
  { label: 'Air-to-Air', value: 'Air-to-Air' },
  { label: 'Support', value: 'Support' },
  { label: 'Dispatch', value: 'Dispatch' },
];

export const WORK_ASSIGNMENT_STATUSES = [
  { label: 'Planned', value: 'Planned' },
  { label: 'Current/Ongoing', value: 'Current/Ongoing' },
  { label: 'Closed', value: 'Closed' },
];

export const STATUS_SUMMARY_REPORT_VERSIONS = [
  { label: 'Initial', value: 'Initial' },
  { label: 'Updated', value: 'Update' },
  { label: 'Final', value: 'Final' },
];

export const STATUS_SUMMARY_INCIDENT_TYPES = [
  { label: 'SAR', value: 'SAR' },
  { label: 'Oil/Petroleum', value: 'Oil/Petroleum' },
  { label: 'Natural Disaster', value: 'Natural Disaster' },
  { label: 'Marine Disaster', value: 'Marine Disaster' },
  {
    label: 'Marine Salvage / Firefighting',
    value: 'Marine Salvage / Firefighting',
  },
  { label: 'Other', value: 'Other' },
];

const IAP_SCOPES = {
  INCIDENT: 'incident',
  ORGANIZATION: 'organization',
} as const;

export const { INCIDENT, ORGANIZATION } = IAP_SCOPES;

export type IapScope = (typeof IAP_SCOPES)[keyof typeof IAP_SCOPES];

export const IAP_DASHBOARD_MODES = {
  CURRENT: 'current',
  FUTURE: 'future',
} as const;

export const VALID_IAP_MODES = Object.values(IAP_DASHBOARD_MODES);

export type IapDashboardMode =
  (typeof IAP_DASHBOARD_MODES)[keyof typeof IAP_DASHBOARD_MODES];

export const ROLE_COLOR_MAP: Record<string, string> = {
  'IC/UC': 'ivory',
  Planning: 'blue',
  'Situation Unit Leader': 'royalblue',
  Operations: 'red',
  'Documentation Unit Leader': 'skyblue',
  'Safety Officer': 'lightgray',
  Logistics: 'orange',
  'Finance/Admin': 'teal',
  'Command Staff': 'whitesmoke',
  PSC: 'dodgerblue',
  OSC: 'crimson',
  RESL: 'steelblue',
  SOFR: 'gainsboro',
  COML: 'ghostwhite',
  'IC/UC Objectives': 'ivory',
  'Command & General Staff Meeting': 'blue',
  'Prepare Tactics': 'royalblue',
  'Tactics Meeting': 'steelblue',
  'Prepare Planning': 'lightgray',
  'Planning Meeting': 'gainsboro',
  'IAP Prep & Approval': 'ghostwhite',
  'Operations Briefing': 'ivory',
};

interface ICSFormMetadataType {
  formName: string;
  label: string;
  description?: string;
  responsibleRoles: ResponsibleRoleType[];
  planningPhases: PlanningPhaseType[];
}

export const ICS_FORMS_METADATA: ICSFormMetadataType[] = [
  {
    formName: 'ICS_201-CG',
    label: 'ICS 201-CG',
    description: 'Initial situation report and response actions taken',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [PLANNING_PHASE.IC_UC_OBJECTIVES],
  },
  {
    formName: 'ICS_202_CG',
    label: 'ICS 202-CG',
    description:
      'Documents the incident objectives, priorities, command direction, and key decisions for the operational period.',
    responsibleRoles: [RESPONSIBLE_ROLE.IC_UC, RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_TACTICS,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_202A_CG',
    label: 'ICS 202A-CG',
    description:
      'Captures Critical Information Requirements (CIRs), essential elements the IC needs to make decisions.',
    responsibleRoles: [RESPONSIBLE_ROLE.IC_UC, RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_TACTICS,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_202B_CG',
    label: 'ICS 202B-CG',
    description:
      'Captures measurable response priorities and constraints that support the operational period objectives.',
    responsibleRoles: [RESPONSIBLE_ROLE.IC_UC, RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_TACTICS,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_203_CG',
    label: 'ICS 203-CG',
    description:
      'Defines the organization for the operational period, including Command, General Staff, and essential units.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_204_CG',
    label: 'ICS 204-CG',
    description:
      'Assignment List describing tactics, resources, reporting locations, and tasking for each division/group.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.IAP_PREP_APPROVAL,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_205_CG',
    label: 'ICS 205-CG',
    description:
      'Communications Plan listing operational radio channels, frequencies, and contact methods.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.IAP_PREP_APPROVAL,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_205A_CG',
    label: 'ICS 205A-CG',
    description:
      'Supplemental Communications List identifying unit-specific communications assignments.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.IAP_PREP_APPROVAL,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_206_CG',
    label: 'ICS 206-CG',
    description:
      'Medical Plan identifying medical response procedures, EMS unit assignments, and casualty transport routes.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.IAP_PREP_APPROVAL,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_207_CG',
    label: 'ICS 207-CG',
    description:
      'Incident Organization Chart showing the structure, activated positions, and assigned personnel.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_211_CG',
    label: 'ICS 211-CG Check-In List',
    description:
      'Records check-in information for personnel and equipment entering the incident.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC, RESPONSIBLE_ROLE.RESL],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_213RR_CG',
    label: 'ICS 213RR-CG Resource Request',
    description:
      'Formal request form used to order resources, track approval, and assign status.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC, RESPONSIBLE_ROLE.RESL],
    planningPhases: [PLANNING_PHASE.OPERATIONS_BRIEFING],
  },
  {
    formName: 'ICS_215_CG',
    label: 'ICS 215-CG Operational Planning Worksheet',
    description:
      'Identifies tactics, resource needs, and capability gaps for the next operational period.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.PREPARE_TACTICS,
      PLANNING_PHASE.TACTICS_MEETING,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
    ],
  },
  {
    formName: 'ICS_230_CG',
    label: 'ICS 230-CG Meeting Schedule',
    description:
      'Daily schedule outlining times and locations for Planning-P meetings and operational briefings.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC, RESPONSIBLE_ROLE.SITL],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_PLANNING,
    ],
  },
  {
    formName: 'ICS_233_CG',
    label: 'ICS 233-CG',
    description:
      'Tracks outstanding action items, assigned responsibilities, and due dates across the IMT.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC, RESPONSIBLE_ROLE.SITL],
    planningPhases: [
      PLANNING_PHASE.IC_UC_OBJECTIVES,
      PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
      PLANNING_PHASE.IAP_PREP_APPROVAL,
      PLANNING_PHASE.OPERATIONS_BRIEFING,
    ],
  },
  {
    formName: 'ICS_234_CG',
    label: 'ICS 234-CG',
    description:
      'Work Analysis Matrix comparing strategies and tactics to operational objectives to determine best options.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC],
    planningPhases: [
      PLANNING_PHASE.PREPARE_TACTICS,
      PLANNING_PHASE.TACTICS_MEETING,
      PLANNING_PHASE.PREPARE_PLANNING,
      PLANNING_PHASE.PLANNING_MEETING,
    ],
  },
  {
    formName: 'ICS_238_CG',
    label: 'ICS 238-CG',
    description:
      'Resource Summary and Tracking Worksheet used to capture assigned, available, and ordered resources.',
    responsibleRoles: [RESPONSIBLE_ROLE.PSC, RESPONSIBLE_ROLE.RESL],
    planningPhases: [PLANNING_PHASE.IAP_PREP_APPROVAL],
  },
];
